// src/features/localization/CoverageSettings.tsx
// Coverage % per locale, per-namespace breakdown. GetTranslationStats.

import { useEffect, useState } from 'react';
import { useI18n, useLocalization } from '@react/hooks';
import { Alert } from '@react/components';
import type { TranslationStats } from '@core/types/localization';
import { errMessage } from './helpers';

export function CoverageSettings() {
  const { t } = useI18n();
  const store = useLocalization();

  const [stats, setStats] = useState<TranslationStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    store
      .GetTranslationStats()
      .then((res) => setStats(res.stats ?? []))
      .catch((e: unknown) => setError(errMessage(e, t('localization.coverage.loadError'))))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) return <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy="true" />;
  if (error) return <Alert variant="error">{error}</Alert>;
  if (stats.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border p-8 text-center">
        <p className="text-sm text-muted-foreground">{t('localization.coverage.empty')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-3">
      {stats.map((s) => (
        <div key={s.locale_code} className="rounded-md border border-border p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">{s.locale_code}</span>
            <span className="text-sm text-muted-foreground">{Math.round(s.coverage_percent)}%</span>
          </div>
          <div className="mt-1.5 h-2 rounded-full bg-muted">
            <div className="h-2 rounded-full bg-primary" style={{ width: `${s.coverage_percent}%` }} />
          </div>
          <div className="mt-2 grid grid-cols-2 gap-1 text-xs text-muted-foreground sm:grid-cols-4">
            <span>{t('localization.coverage.total', { count: s.total_keys })}</span>
            <span>{t('localization.coverage.translated', { count: s.translated_keys })}</span>
            <span>{t('localization.coverage.reviewed', { count: s.reviewed_keys })}</span>
            <span>{t('localization.coverage.approved', { count: s.approved_keys })}</span>
          </div>
          {s.namespace_stats.length > 0 && (
            <div className="mt-2 space-y-1 border-t border-border pt-2">
              {s.namespace_stats.map((ns) => (
                <div key={ns.namespace_slug} className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{ns.namespace_slug}</span>
                  <span className="text-foreground">{Math.round(ns.coverage_percent)}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
